import { useState, useEffect, useCallback } from 'react'
import { Link } from 'react-router-dom'
import {
  MapPin,
  Phone,
  Mail,
  Calendar,
  Clock,
  Car,
  Compass,
  Info,
  CheckCircle2,
  ZoomIn,
  X,
  ChevronLeft,
  ChevronRight,
  Eye,
  ShieldCheck
} from 'lucide-react'
import { scrapedData, clinicPhotos } from '../data'
import { renderFormattedText } from '../utils/textFormatter'
import SEO from '../components/SEO'
import { imageUrl, srcSet } from '../lib/images'

export default function Clinic() {
  const { omKlinikken } = scrapedData
  const [selectedPhotoIndex, setSelectedPhotoIndex] = useState(null)

  // Lightbox keyboard controls
  const handleKeyDown = useCallback((e) => {
    if (selectedPhotoIndex === null) return
    if (e.key === 'Escape') {
      setSelectedPhotoIndex(null)
    } else if (e.key === 'ArrowRight') {
      setSelectedPhotoIndex((prev) => (prev + 1) % clinicPhotos.length)
    } else if (e.key === 'ArrowLeft') {
      setSelectedPhotoIndex((prev) => (prev - 1 + clinicPhotos.length) % clinicPhotos.length)
    }
  }, [selectedPhotoIndex])

  useEffect(() => {
    if (selectedPhotoIndex !== null) {
      window.addEventListener('keydown', handleKeyDown)
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = 'unset'
    }
    return () => {
      window.removeEventListener('keydown', handleKeyDown)
      document.body.style.overflow = 'unset'
    }
  }, [selectedPhotoIndex, handleKeyDown])

  return (
    <div className="space-y-20 pb-16">
      <SEO
        title="Klinikken i Gravene 28B, Viborg | Rørbækklinikken"
        description="Besøg Rørbækklinikken i Gravene 28B, 8800 Viborg. Se billeder af vores rolige behandlingsrum, venteværelse og faciliteter. Helhedsorienteret behandling med Det Hele Menneske."
      />
      
      {/* Page Header */}
      <section className="bg-[#F5F0E6]/40 py-16 border-b border-[#E6DFD3]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center space-y-4">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#1E352F]/5 border border-[#1E352F]/10 text-[#1E352F] text-xs font-semibold tracking-wider uppercase">
            <span className="w-2 h-2 rounded-full bg-[#eebb53]" />
            Gravene 28B, 8800 Viborg
          </div>
          <h1 className="font-serif text-4xl sm:text-5xl font-bold text-[#1E352F]">
            Klinikken i Viborg
          </h1>
          <p className="text-base sm:text-lg text-[#1E352F]/80 leading-relaxed">
            Velkommen indenfor hos Rørbækklinikken I/S. Vi har skabt en rolig, tryg og professionel oase i hjertet af Viborg, hvor din krop og dit velvære er i absolut centrum.
          </p>
        </div>
      </section>

      {/* Visual Presentation & Verbatim Text */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          
          {/* Left: Featured Image & Address Card */}
          <div className="lg:col-span-5 space-y-6">
            <div className="relative group rounded-2xl overflow-hidden shadow-md border border-[#E6DFD3] aspect-[4/3] bg-stone-100">
              <img
                src="https://assets.auxodev.dk/cdn-cgi/image/width=1200,quality=92,format=auto/assets/bd5a76fd-d82e-431d-9e97-5b07d543e9b6/ra-rba-kklinikken-forside.png"
                srcSet="https://assets.auxodev.dk/cdn-cgi/image/width=600,quality=92,format=auto/assets/bd5a76fd-d82e-431d-9e97-5b07d543e9b6/ra-rba-kklinikken-forside.png 600w, https://assets.auxodev.dk/cdn-cgi/image/width=1000,quality=92,format=auto/assets/bd5a76fd-d82e-431d-9e97-5b07d543e9b6/ra-rba-kklinikken-forside.png 1000w, https://assets.auxodev.dk/cdn-cgi/image/width=1600,quality=92,format=auto/assets/bd5a76fd-d82e-431d-9e97-5b07d543e9b6/ra-rba-kklinikken-forside.png 1600w"
                sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 520px"
                alt="Rørbækklinikken - Gravene 28B Viborg"
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                loading="eager"
              />
              <button
                onClick={() => setSelectedPhotoIndex(0)}
                className="absolute top-4 right-4 bg-white/90 hover:bg-white text-[#1E352F] p-2 rounded-full shadow-md transition-all cursor-pointer"
                title="Se i fuld skærm"
                aria-label="Se billede i fuld skærm"
              >
                <ZoomIn className="w-4 h-4" />
              </button>
            </div>

            {/* Address & Contact Card */}
            <div className="bg-[#F5F0E6] rounded-2xl p-6 border border-[#E6DFD3] space-y-4">
              <div className="flex items-center justify-between border-b border-[#E6DFD3] pb-3">
                <h3 className="font-serif text-xl font-bold text-[#1E352F]">
                  Rørbækklinikken I/S
                </h3>
                <span className="text-xs bg-[#1E352F] text-white px-2.5 py-1 rounded-full font-medium">
                  Viborg Centrum
                </span>
              </div>
              <ul className="space-y-3 text-sm text-[#1E352F]/80">
                <li className="flex items-center gap-3">
                  <MapPin className="w-5 h-5 text-[#eebb53] shrink-0" />
                  <span>Gravene 28B, 8800 Viborg</span>
                </li>
                <li className="flex items-center gap-3">
                  <Phone className="w-5 h-5 text-[#eebb53] shrink-0" />
                  <span>Tlf: <a href="tel:86639393" className="hover:underline font-semibold">86 63 93 93</a></span>
                </li>
                <li className="flex items-center gap-3">
                  <Mail className="w-5 h-5 text-[#eebb53] shrink-0" />
                  <span>Email: <a href="mailto:im@muskelterapi.com" className="hover:underline">im@muskelterapi.com</a></span>
                </li>
              </ul>
              <div className="pt-2">
                <a
                  href="https://roerbaekklinikken.planway.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full bg-[#1E352F] hover:bg-[#2D4A43] text-white py-2.5 px-4 rounded-xl text-xs font-semibold flex items-center justify-center gap-2 transition-colors shadow-sm"
                >
                  <Calendar className="w-4 h-4 text-[#eebb53]" />
                  Bestil tid online
                </a>
              </div>
            </div>
          </div>

          {/* Right: Verbatim Text */}
          <div className="lg:col-span-7 space-y-6">
            <div className="inline-block px-3 py-1 rounded-full bg-[#eebb53]/10 text-[#eebb53] text-xs font-bold uppercase tracking-wider">
              Vores værdier og rammer
            </div>
            <h2 className="font-serif text-3xl font-bold text-[#1E352F]">
              {omKlinikken.title}
            </h2>
            <div className="space-y-4 text-sm sm:text-base text-[#1E352F]/80 leading-relaxed">
              {renderFormattedText(omKlinikken.content)}
            </div>
          </div>

        </div>
      </section>

      {/* 2. PHOTO GALLERY SECTION - Billeder fra klinikken */}
      <section className="bg-[#FDFBF7] py-16 border-y border-[#E6DFD3]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-10">
          
          {/* Section Header */}
          <div className="text-center max-w-3xl mx-auto space-y-3">
            <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#1E352F]/5 text-[#1E352F] text-xs font-semibold tracking-wider uppercase">
              <Eye className="w-3.5 h-3.5 text-[#eebb53]" />
              Kig indenfor i Gravene 28B
            </div>
            <h2 className="font-serif text-3xl sm:text-4xl font-bold text-[#1E352F]">
              Billeder fra Rørbækklinikken
            </h2>
          </div>

          {/* Photo Grid - Bare billeder uden tekst */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {clinicPhotos.map((photo, idx) => (
              <div
                key={photo.id}
                onClick={() => setSelectedPhotoIndex(idx)}
                className="group relative rounded-2xl overflow-hidden border border-[#E6DFD3] bg-stone-100 shadow-sm hover:shadow-md transition-all duration-300 cursor-pointer aspect-[4/3]"
              >
                <img
                  src={imageUrl(photo.url, 'width=1000,quality=90,format=auto')}
                  srcSet={srcSet(photo.url, [500, 800, 1200])}
                  sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
                  alt={photo.alt}
                  loading="lazy"
                  decoding="async"
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
              </div>
            ))}
          </div>

        </div>
      </section>

      {/* Practical Information (Parking, Find Us) */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          
          {/* Left: Practical Info */}
          <div className="lg:col-span-6 space-y-8">
            <div className="space-y-2">
              <div className="inline-block px-3 py-1 rounded-full bg-[#eebb53]/10 text-[#eebb53] text-xs font-bold uppercase tracking-wider">
                Gode råd til dit besøg
              </div>
              <h2 className="font-serif text-3xl font-bold text-[#1E352F]">
                Praktisk information
              </h2>
            </div>

            <div className="space-y-6">
              {/* Parking */}
              <div className="flex gap-4 items-start bg-white p-5 rounded-xl border border-[#E6DFD3] shadow-sm">
                <div className="p-3 rounded-lg bg-[#F5F0E6] text-[#eebb53] shrink-0">
                  <Car className="w-6 h-6" />
                </div>
                <div className="space-y-1">
                  <h3 className="font-serif text-lg font-bold text-[#1E352F]">
                    Parkering og adgang
                  </h3>
                  <p className="text-sm text-[#1E352F]/70 leading-relaxed">
                    Der er gode parkeringsmuligheder i umiddelbar nærhed af klinikken. Du kan blandt andet parkere på de offentlige parkeringspladser ved Gravene eller i de nærliggende parkeringshuse i Viborg centrum (husk at tjekke p-skiven/parkerings-app).
                  </p>
                </div>
              </div>

              {/* How to find us */}
              <div className="flex gap-4 items-start bg-white p-5 rounded-xl border border-[#E6DFD3] shadow-sm">
                <div className="p-3 rounded-lg bg-[#F5F0E6] text-[#eebb53] shrink-0">
                  <Compass className="w-6 h-6" />
                </div>
                <div className="space-y-1">
                  <h3 className="font-serif text-lg font-bold text-[#1E352F]">
                    Find vej til klinikken
                  </h3>
                  <p className="text-sm text-[#1E352F]/70 leading-relaxed">
                    Klinikken ligger på Gravene 28B, 8800 Viborg. Indgangen er let at finde fra gaden, og der er tydelig skiltning ved døren. Hvis du har svært ved at finde os, er du altid velkommen til at ringe på tlf. 86 63 93 93.
                  </p>
                </div>
              </div>

              {/* Arrival */}
              <div className="flex gap-4 items-start bg-white p-5 rounded-xl border border-[#E6DFD3] shadow-sm">
                <div className="p-3 rounded-lg bg-[#F5F0E6] text-[#eebb53] shrink-0">
                  <Info className="w-6 h-6" />
                </div>
                <div className="space-y-1">
                  <h3 className="font-serif text-lg font-bold text-[#1E352F]">
                    Ankomst og ventetid
                  </h3>
                  <p className="text-sm text-[#1E352F]/70 leading-relaxed">
                    Vi anbefaler, at du ankommer ca. 5-10 minutter før din aftalte tid, så du kan nå at sætte dig i venteværelset, få en tår vand eller te og puste ud. Vi bestræber os altid på at overholde tidsplanen, så du ikke oplever unødig ventetid.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Right: Map Graphic & Contact Card */}
          <div className="lg:col-span-6 space-y-6">
            <div className="bg-[#F5F0E6] rounded-2xl p-8 border border-[#E6DFD3] space-y-6">
              <div className="space-y-2">
                <h3 className="font-serif text-xl font-bold text-[#1E352F]">
                  Find os på kortet
                </h3>
                <p className="text-sm text-[#1E352F]/70">
                  Gravene 28B, 8800 Viborg
                </p>
              </div>

              {/* Visual Map graphic */}
              <div className="bg-white rounded-xl p-8 border border-[#E6DFD3] flex flex-col items-center justify-center text-center space-y-4 aspect-[16/10] relative overflow-hidden">
                <div className="w-16 h-16 rounded-full bg-[#1E352F]/5 flex items-center justify-center text-[#eebb53]">
                  <MapPin className="w-8 h-8" />
                </div>
                <div className="space-y-1 z-10">
                  <p className="text-base font-bold text-[#1E352F]">Gravene 28B, 8800 Viborg</p>
                  <p className="text-xs text-[#1E352F]/60">Lige overfor Gravene P-plads i Viborg centrum</p>
                </div>
                <a
                  href="https://maps.google.com/?q=Gravene+28A,+8800+Viborg"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 bg-[#eebb53] hover:bg-[#dca63d] text-white px-5 py-2.5 rounded-full text-xs font-bold transition-all shadow-sm z-10"
                >
                  <Compass className="w-4 h-4" />
                  Åben i Google Maps
                </a>
              </div>

              {/* Opening hours snippet */}
              <div className="bg-white rounded-xl p-5 border border-[#E6DFD3] flex items-center justify-between text-xs text-[#1E352F]/80">
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4 text-[#eebb53]" />
                  <span>Mandag - Fredag</span>
                </div>
                <span className="font-bold text-[#1E352F]">Telefontid 08:00 - 17:00</span>
              </div>
            </div>
          </div>

        </div>
      </section>

      {/* CTA Section */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-[#1E352F] rounded-2xl text-[#F5F0E6] p-8 sm:p-12 text-center space-y-6 border border-[#2D4A43] relative overflow-hidden">
          <div className="relative z-10 space-y-4">
            <h2 className="font-serif text-2xl sm:text-3xl font-bold text-white">
              Klar til at besøge os i klinikken?
            </h2>
            <p className="text-sm sm:text-base text-[#F5F0E6]/80 max-w-2xl mx-auto leading-relaxed">
              Find en ledig tid, der passer dig, og bestil din behandling online i dag. Det er hurtigt, nemt og sikkert.
            </p>
            <div className="pt-2 flex flex-col sm:flex-row items-center justify-center gap-4">
              <a
                href="https://roerbaekklinikken.planway.com/"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-[#eebb53] hover:bg-[#dca63d] text-white px-8 py-3.5 rounded-full text-base font-semibold transition-colors shadow-sm"
              >
                <Calendar className="w-5 h-5" />
                Bestil tid online
              </a>
              <Link
                to="/kontakt"
                className="inline-flex items-center gap-2 bg-transparent hover:bg-white/10 text-white border border-white/30 px-6 py-3.5 rounded-full text-base font-semibold transition-colors"
              >
                Kontakt os
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* LIGHTBOX MODAL - Rent billede uden tekst */}
      {selectedPhotoIndex !== null && (
        <div
          className="fixed inset-0 z-50 bg-black/90 backdrop-blur-sm flex items-center justify-center p-4 sm:p-8 animate-fadeIn"
          onClick={() => setSelectedPhotoIndex(null)}
          role="dialog"
          aria-modal="true"
        >
          <div
            className="relative max-w-5xl w-full flex flex-col items-center"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close Button */}
            <button
              onClick={() => setSelectedPhotoIndex(null)}
              className="self-end mb-2 p-2 rounded-full text-white/80 hover:text-white hover:bg-white/10 transition-colors cursor-pointer"
              aria-label="Luk visning"
            >
              <X className="w-6 h-6" />
            </button>

            {/* Image View */}
            <div className="relative rounded-2xl overflow-hidden bg-black flex items-center justify-center max-h-[82vh] w-full shadow-2xl border border-white/10">
              <img
                src={imageUrl(clinicPhotos[selectedPhotoIndex].url, 'width=1920,quality=95,format=auto')}
                srcSet={srcSet(clinicPhotos[selectedPhotoIndex].url, [1000, 1600, 2048])}
                sizes="90vw"
                alt={clinicPhotos[selectedPhotoIndex].alt}
                decoding="sync"
                className="max-h-[82vh] w-auto max-w-full object-contain mx-auto"
              />

              {/* Prev Button */}
              <button
                onClick={() => setSelectedPhotoIndex((selectedPhotoIndex - 1 + clinicPhotos.length) % clinicPhotos.length)}
                className="absolute left-3 top-1/2 -translate-y-1/2 bg-black/60 hover:bg-black/90 text-white p-3 rounded-full transition-all cursor-pointer backdrop-blur-sm border border-white/20"
                aria-label="Forrige billede"
              >
                <ChevronLeft className="w-6 h-6" />
              </button>

              {/* Next Button */}
              <button
                onClick={() => setSelectedPhotoIndex((selectedPhotoIndex + 1) % clinicPhotos.length)}
                className="absolute right-3 top-1/2 -translate-y-1/2 bg-black/60 hover:bg-black/90 text-white p-3 rounded-full transition-all cursor-pointer backdrop-blur-sm border border-white/20"
                aria-label="Næste billede"
              >
                <ChevronRight className="w-6 h-6" />
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  )
}
